import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

import { shouldIgnoreOpeningPointer } from "@/lib/touchOpenClose";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { computeAnchoredListPanel, FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";

export type HubTouchSelectOption = {
  value: string;
  label: string;
  /** Second line under the label in the open list (stock, reachability). */
  detail?: string;
  /** Shown to the left of the label in the trigger and list (for example a status dot). */
  leading?: ReactNode;
};

const TRIGGER_CLASS = {
  compact:
    "h-8 min-w-[12rem] max-w-[20rem] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900",
  default:
    "h-10 w-full min-w-[12rem] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900",
  touch:
    "h-12 w-full min-w-[13rem] rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900",
} as const;

const ICON_TRIGGER_CLASS =
  "inline-flex h-10 w-10 min-w-10 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white px-2.5 text-slate-900";

const LIST_MAX_HEIGHT_PX = 280;

/**
 * Exact-value picker that stays open on tablet tap. Native `<select>` on Android WebView
 * opens and dismisses on the same gesture when the page re-renders (labor heartbeat, presence).
 */
export function HubTouchSelect({
  id,
  value,
  onChange,
  options,
  disabled = false,
  className,
  triggerClassName,
  panelMinWidthPx,
  listItemClassName,
  size = "default",
  triggerMode = "labeled",
  triggerIcon,
  onOpenChange,
  placeholder = "Select…",
  "aria-label": ariaLabel,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly HubTouchSelectOption[];
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  /** Minimum dropdown panel width in pixels (defaults to trigger width, at least 192). */
  panelMinWidthPx?: number;
  listItemClassName?: string;
  size?: keyof typeof TRIGGER_CLASS;
  triggerMode?: "labeled" | "icon";
  triggerIcon?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  /** Trigger text when nothing is selected. Not a list row. */
  placeholder?: string;
  "aria-label"?: string;
}): JSX.Element {
  const generatedId = useId();
  const listboxId = `${id || generatedId}-listbox`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openGuardRef = useRef<{ openedAtMs: number; pointerId: number | null }>({
    openedAtMs: 0,
    pointerId: null,
  });
  const openedByPointerRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [panelBox, setPanelBox] = useState<ReturnType<typeof computeAnchoredListPanel> | null>(null);
  const touchListPick = useTouchListPickSession(open);

  const selected = options.find((option) => option.value === value);
  const triggerLabel = selected?.label ?? (value !== "" ? value : placeholder);

  const reposition = (): void => {
    const button = triggerRef.current;
    if (button == null) return;
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: button.getBoundingClientRect(),
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
        minWidth: Math.max(button.offsetWidth, panelMinWidthPx ?? 192),
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    reposition();
    const onLayout = (): void => {
      reposition();
    };
    return registerViewportLayoutListeners(onLayout);
  }, [open, options.length, panelMinWidthPx, value]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [onOpenChange, open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) {
        return;
      }
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function toggleOpen(pointerId: number | null): void {
    if (disabled) return;
    setOpen((current) => {
      const next = !current;
      if (next) {
        openGuardRef.current = { openedAtMs: Date.now(), pointerId };
      }
      return next;
    });
  }

  function pick(next: string): void {
    onChange(next);
    setOpen(false);
  }

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        className={cn(
          "text-left touch-manipulation",
          triggerMode === "icon"
            ? "inline-flex items-center justify-center"
            : "flex items-center justify-between gap-2",
          "focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500",
          "disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
          triggerMode === "icon" ? ICON_TRIGGER_CLASS : TRIGGER_CLASS[size],
          triggerClassName,
        )}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          openedByPointerRef.current = true;
          toggleOpen(event.pointerId);
        }}
        onClick={(event) => {
          if (openedByPointerRef.current) {
            openedByPointerRef.current = false;
            event.preventDefault();
            return;
          }
          toggleOpen(null);
        }}
      >
        {triggerMode === "icon" ? (
          triggerIcon ?? null
        ) : (
          <>
            <span
              className={cn(
                "flex min-w-0 items-center gap-2 truncate",
                selected == null && value === "" && "text-slate-400",
              )}
            >
              {selected?.leading}
              <span className="min-w-0 truncate">{triggerLabel}</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          </>
        )}
      </button>
      {open && panelBox != null && portalTarget != null
        ? createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              data-hub-touch-select-panel=""
              className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <ul
                className="overflow-y-auto overscroll-y-contain py-1 touch-pan-y [-webkit-overflow-scrolling:touch]"
                style={{ maxHeight: panelBox.listContentMaxHeight }}
              >
                {options.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-slate-500">No options.</li>
                ) : (
                  options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                      <li key={option.value} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={cn(
                            "flex min-h-12 w-full items-center px-3 text-left text-base touch-manipulation hover:bg-slate-50",
                            listItemClassName,
                            isSelected && "bg-slate-50 font-medium",
                          )}
                          onPointerDown={(event) => {
                            touchListPick.onListItemPointerDown(event, () => pick(option.value));
                          }}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            {option.leading}
                            <span className="min-w-0">
                              <span className="block">{option.label}</span>
                              {option.detail != null && option.detail !== "" ? (
                                <span className="block text-xs font-normal text-slate-500">{option.detail}</span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>,
            portalTarget,
          )
        : null}
    </div>
  );
}
